import type { EditorConfig, NodeKey, SerializedTextNode, Spread } from 'lexical';
import { TextNode } from 'lexical';

export type ReferenceKind = 'video' | 'template' | 'brand-kit' | 'voice' | 'blueprint';

export interface Reference {
    kind: ReferenceKind;
    id: string;
    name: string;
    thumbUrl?: string | null;
    detail?: string | null;
    swatches?: string[];
    previewUrl?: string | null;
}

const LEXICAL_NODE_TYPE = 'reference-mention';
const LEXICAL_NODE_VERSION = 1;

export type SerializedReferenceMentionNode = Spread<
    {
        referenceKind: ReferenceKind;
        referenceId: string;
        referenceName: string;
        type: typeof LEXICAL_NODE_TYPE;
        version: typeof LEXICAL_NODE_VERSION;
    },
    SerializedTextNode
>;

/**
 * A pill referencing a Lumen5 object. UI only: messages are sent as plain text
 * where each mention becomes `@[kind:id|Name]` (referenceMentionSerialization).
 * The leading "@" comes from CSS, so the node's text stays the bare name.
 */
export class ReferenceMentionNode extends TextNode {
    __referenceKind: ReferenceKind;
    __referenceId: string;
    __referenceName: string;

    constructor(kind: ReferenceKind, id: string, name: string, key?: NodeKey) {
        super(name, key);
        this.__referenceKind = kind;
        this.__referenceId = id;
        this.__referenceName = name;
    }

    static override getType() {
        return LEXICAL_NODE_TYPE;
    }

    static override clone(node: ReferenceMentionNode): ReferenceMentionNode {
        return new ReferenceMentionNode(
            node.__referenceKind,
            node.__referenceId,
            node.__referenceName,
            node.__key,
        );
    }

    getReferenceKind(): ReferenceKind {
        return this.__referenceKind;
    }

    getReferenceId(): string {
        return this.__referenceId;
    }

    getReferenceName(): string {
        return this.__referenceName;
    }

    override createDOM(config: EditorConfig): HTMLElement {
        const dom = super.createDOM(config);
        dom.className = 'reference-mention';
        // Read by the hover/click plugin via event delegation, so it never has
        // to reach into Lexical state.
        dom.setAttribute('data-reference-kind', this.__referenceKind);
        dom.setAttribute('data-reference-id', this.__referenceId);
        dom.setAttribute('data-reference-name', this.__referenceName);
        return dom;
    }

    static override importJSON(
        serializedNode: SerializedReferenceMentionNode,
    ): ReferenceMentionNode {
        return new ReferenceMentionNode(
            serializedNode.referenceKind,
            serializedNode.referenceId,
            serializedNode.referenceName,
        );
    }

    override exportJSON(): SerializedReferenceMentionNode {
        return {
            ...super.exportJSON(),
            referenceKind: this.__referenceKind,
            referenceId: this.__referenceId,
            referenceName: this.__referenceName,
            type: LEXICAL_NODE_TYPE,
            version: LEXICAL_NODE_VERSION,
        };
    }
}

export function $createReferenceMentionNode(reference: Reference): ReferenceMentionNode {
    const node = new ReferenceMentionNode(reference.kind, reference.id, reference.name);
    // 'token' not 'segmented': segmented deletes one word at a time, so a
    // multi-word name would take several backspaces to remove.
    node.setMode('token');
    return node;
}

export function $isReferenceMentionNode(node: unknown): node is ReferenceMentionNode {
    return node instanceof ReferenceMentionNode;
}
